import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';

import { useStatusBarSync } from '../hooks/useStatusBarSync';

type Theme = 'light' | 'dark';

/** Stored preference: 'light' | 'dark' = user choice; 'auto' or missing = follow time of day */
const THEME_KEY = 'app-theme';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

/** Day = 6:00–18:00 local time → light; otherwise dark. */
function getTimeBasedTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const hour = new Date().getHours();
  return hour >= 6 && hour < 18 ? 'light' : 'dark';
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
    return getTimeBasedTheme();
  });
  const isAutoRef = useRef(() => {
    const s = localStorage.getItem(THEME_KEY);
    return s !== 'light' && s !== 'dark';
  });

  // Sync Android status bar style/colour whenever the theme changes
  useStatusBarSync(theme);

  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, [theme]);

  // Sync meta theme-color for PWA/Android Chrome (matches light/dark mockups)
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) meta.content = theme === 'dark' ? '#19191A' : '#8B80F9';
  }, [theme]);

  // Persist only explicit user choice (light/dark). When auto, don't overwrite 'auto' with current theme every time.
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(THEME_KEY, newTheme);
    } catch (_) {}
    isAutoRef.current = () => false;
  };

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
  };

  // Time-aware: when preference is 'auto' (or unset), refresh theme every minute so day/night transition updates the UI
  useEffect(() => {
    const interval = setInterval(() => {
      const saved = localStorage.getItem(THEME_KEY);
      if (saved === 'light' || saved === 'dark') return;
      const next = getTimeBasedTheme();
      setThemeState(prev => (prev !== next ? next : prev));
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== THEME_KEY) return;
      const v = e.newValue;
      if (v === 'light' || v === 'dark') {
        setThemeState(v);
        isAutoRef.current = () => false;
      } else {
        setThemeState(getTimeBasedTheme());
        isAutoRef.current = () => true;
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};